import * as React from 'react';
import { IconButton } from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import { TK } from '../../store/translations/translationKeys';
import AppointmentEditForm from '../../modules/AppointmentEditForm';
import ClientEditForm from '../../pages/Clients/ClientEditForm';
import UserEditForm from '../../pages/Users/UserEditForm';
import ProfessionalEditForm from '../../pages/Professionals/ProfessionalEditForm';
import { Translate, useTranslations } from '../../store/translations/hooks';
import { AppointmentEntity, ChangeLogEntity, ClientEntity, ClientOrderEntity, ClientPackEntity, CompanyEntity, Entity, EntityChange, EntityType, ExpenseEntity, StockEntity, FileEntity, PaymentEntity, ProfessionalEntity, ReportEntity, UserEntity } from '../../store/entities/state';
import AppointmentCreateForm from '../../modules/AppointmentCreateForm';
import ClientCreateForm from '../../pages/Clients/ClientCreateForm';
import UserCreateForm from '../../pages/Users/UserCreateForm';
import ProfessionalCreateForm from '../../pages/Professionals/ProfessionalCreateForm';
import { useDispatch, useSelector } from 'react-redux';
import { getEntitiesBeingChanged, getReportsForClient } from '../../store/entities/selectors';
import { entityCreatingCancel, entityEditing } from '../../store/entities/actions';
import AppointmentProfessionalView from '../../modules/AppointmentProfessionalView';
import * as session from '../../store/session/selectors';
import ReportCreateForm from '../../modules/ReportCreateForm';
import { getLatestPackTemplates } from '../../store/settings/selectors';
import ClientPackCreateForm from '../../modules/ClientPackCreateForm';
import ClientPackEditForm from '../../modules/ClientPackEditForm';
import { useLocation } from 'react-router';
import { paths } from '../constants';
import { GeneralSettingsData, LoggedUser } from '../../models';
import ReportEditForm from '../../modules/ReportEditForm';
import ReportViewForm from '../../modules/ReportViewForm';
import { useEffect, useRef, useState } from 'react';
import ExpenseEditForm from '../../pages/Billing/ExpenseEditForm';
import ExpenseCreateForm from '../../pages/Billing/ExpenseCreateForm';
import StockItemEditForm from '../../pages/Billing/StockItemEditForm';
import StockItemCreateForm from '../../pages/Billing/StockItemCreateForm';
import ClientOrderEditForm from '../../pages/Clients/ClientOrderEditForm';
import { useGeneralSettings } from '../../store/settings/hooks';
import { serviceName } from '../../shared/appointmint/appointment';
import { useModalZIndex } from '../../shared/modalStack';

const getModalTitle = (entityType: EntityType, entity: EntityChange<Entity>, t: Translate<TK>, myUser: LoggedUser, generalSettings: GeneralSettingsData) => {
    switch (entityType) {
        case 'appointments': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.appointment)}`
            : !myUser.professionalId ? `${t(TK.appointment)} - ${(entity.value as AppointmentEntity).clientName}`
                : myUser.professionalId === (entity.value as AppointmentEntity).professionalId ? (serviceName((entity.value as AppointmentEntity).type, generalSettings)) 
                : `${serviceName((entity.value as AppointmentEntity).type, generalSettings)} (${(entity.value as AppointmentEntity).professionalName ?? ''})` 
        case 'clients': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.client)}`
            : `${t(TK.client)} - ${(entity.value as ClientEntity).name ?? ''}`
        case 'users': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.user)}`
            : `${t(TK.user)} - ${(entity.value as UserEntity).username ?? ''}`
        case 'professionals': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.professional)}`
            : `${t(TK.professional)} - ${(entity.value as ProfessionalEntity).name ?? ''}`
        case 'reports': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.report)}`
            :  `${t(TK.report)} - ${(entity.value as ReportEntity).clientName ?? ''}`
        case 'clientPacks': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.pack)}/${t(TK.subscription)}`
            : `${t((entity.value as ClientPackEntity)?.isSubscription ? TK.subscription : TK.pack)} - ${(entity.value as ClientPackEntity).clientName ?? ''}`
        case 'files': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.file)}`
            : t(TK.file)
        case 'communications': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.communications)}`
            : t(TK.communications)
        case 'payments': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.payments)}`
            : `${t(TK.payments)} - ${(entity.value as PaymentEntity).clientName ?? ''}`
        case 'changeLogs': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.changeLog)}`
            : `${t(TK.changeLog)} - ${(entity.value as ChangeLogEntity).username ?? ''}`
        case 'companies': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.company)}`
            : `${t(TK.company)} - ${(entity.value as CompanyEntity).name ?? ''}`
        case 'expenses': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.expense)}`
            : `${t(TK.expense)} - ${(entity.value as ExpenseEntity).name ?? ''}`
        case 'stock': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.stockItem)}`
            : `${t(TK.stockItem)} - ${(entity.value as StockEntity).name ?? ''}`
        case 'orders': return entity.action === 'creating'
            ? `${t(TK.create)} - ${t(TK.orders)}`
            : `${t(TK.orders)} — ${(entity.value as ClientOrderEntity).clientName} (${(entity.value as ClientOrderEntity).creationDate.slice(0, 10)})`
    }
}

const popupOrder: EntityType[] = [
    'clientPacks',
    'reports',
    'appointments',
    'clients',
    'professionals',
    'users',
    'expenses',
    'stock',
    'orders',
]

const hideEditionPopupForPages: Record<EntityType, string[]> = (() => ({
    ['clients']: [paths.clients()],
    ['users']: [paths.users(), paths.profile(), paths.settingsUsers()],
    ['professionals']: [paths.professionals(), paths.users(), paths.profile()],
    ['appointments']: [],
    ['clientPacks']: [],
    ['reports']: [],
    ['files']: [],
    ['communications']: [],
    ['payments']: [],
    ['changeLogs']: [],
    ['companies']: [],
    ['expenses']: [],
    ['stock']: [],
    ['orders']: [],
    ['prescriptions']: [],
}))()

interface FullscreenToggleProps {
    containerRef: React.RefObject<HTMLDivElement>
    innerRef: React.RefObject<HTMLDivElement>
    modalMaxWidth: string
}

const FullscreenToggle: React.FC<FullscreenToggleProps> = ({ containerRef, innerRef, modalMaxWidth }) => {
    const [isFullscreen, setIsFullscreen] = useState(false)

    const handleToggle = () => {
        const newVal = !isFullscreen
        setIsFullscreen(newVal)
        if (containerRef.current) {
            containerRef.current.style.alignItems = newVal ? 'stretch' : 'flex-start'
            containerRef.current.style.overflowY = newVal ? 'hidden' : 'auto'
            containerRef.current.style.backgroundColor = newVal ? '' : 'rgba(0,0,0,0.5)'
            containerRef.current.style.padding = newVal ? '0' : 'var(--spacer-M)'
        }
        if (innerRef.current) {
            innerRef.current.style.width = '100%'
            innerRef.current.style.maxWidth = newVal ? '100%' : modalMaxWidth
            innerRef.current.style.maxHeight = newVal ? '100vh' : ''
            innerRef.current.style.overflow = newVal ? 'auto' : ''
            innerRef.current.style.borderRadius = newVal ? '0' : '0.3rem'
            innerRef.current.style.boxShadow = newVal ? 'none' : '0 0.5rem 1rem rgba(0,0,0,0.5)'
        }
    }

    return (
        <IconButton size="small" onClick={handleToggle}>
            {isFullscreen ? <FullscreenExitIcon/> : <FullscreenIcon/>}
        </IconButton>
    )
}

const EntityModal: React.FC = () => {

    const dispatch = useDispatch();

    const t = useTranslations();

    const location = useLocation();
    const generalSettings = useGeneralSettings();
    const accountTab = new URLSearchParams(location.search).get('tab');
    const onAccountUsersTab = location.pathname === paths.account() && accountTab === 'users';
    
    const myUser = useSelector(session.getMyUser)
    const isProfessional = !!myUser?.professionalId

    const packsSettings = useSelector(getLatestPackTemplates)

    const changing = useSelector(getEntitiesBeingChanged);

    const packSettings = changing['clientPacks']
        && packsSettings?.find(pack => (pack.templateId ?? pack.id) === changing['clientPacks']?.value.packTemplateId && pack.version === changing['clientPacks']?.value.packVersion)

    const topEntityType = popupOrder.find(type => !!changing[type])
    const topEntity = topEntityType && changing[topEntityType]

    const onClose = React.useCallback(() => { topEntityType && dispatch(entityCreatingCancel(topEntityType)) }, [dispatch, changing]);

    const hasChanged = changing['reports']?.hasChanged ||
        changing['reports']?.hasChanged ||
        changing['appointments']?.hasChanged ||
        changing['clientPacks']?.hasChanged ||
        changing['clients']?.hasChanged ||
        changing['professionals']?.hasChanged ||
        changing['users']?.hasChanged ||
        changing['expenses']?.hasChanged ||
        changing['stock']?.hasChanged ||
        changing['orders']?.hasChanged

    // Report keyboard navigation
    const currentReportClientId = changing['reports']?.value?.clientId
    const clientReports = useSelector(getReportsForClient(currentReportClientId ?? ''))
    const sortedReports = React.useMemo(() =>
        [...clientReports].sort((a, b) => (a.creationDate ?? '') > (b.creationDate ?? '') ? -1 : 1),
        [clientReports]
    )

    const canNavigateReports = changing['reports']
        && changing['reports'].action !== 'creating'
        && !changing['reports'].hasChanged
        && sortedReports.length > 1

    // Keyboard navigation for reports
    useEffect(() => {
        if (!canNavigateReports || !changing['reports']) return

        const currentReportId = changing['reports'].value.id
        const currentIndex = sortedReports.findIndex(r => r.id === currentReportId)

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return

            // Don't navigate if user is typing in an input
            const target = e.target as HTMLElement
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

            if (e.key === 'ArrowLeft' && currentIndex > 0) {
                e.preventDefault()
                dispatch(entityEditing('reports', sortedReports[currentIndex - 1].id))
            } else if (e.key === 'ArrowRight' && currentIndex < sortedReports.length - 1) {
                e.preventDefault()
                dispatch(entityEditing('reports', sortedReports[currentIndex + 1].id))
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [canNavigateReports, changing, sortedReports, dispatch])

    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            event.returnValue = ""
        }

        if (hasChanged) {
            window.addEventListener("beforeunload", handler)
            return () => {
                window.removeEventListener("beforeunload", handler)
            }
        }
    }, [hasChanged])

    const form = 
        !changing ? <></>
        : changing['reports'] ? (changing['reports'].action === 'creating' 
            ? <ReportCreateForm />
            : changing['reports'].hasChanged 
                ? <ReportEditForm />
                : <ReportViewForm />)
        : changing['appointments'] ? (changing['appointments'].action === 'creating'
            ? <AppointmentCreateForm />
            : !isProfessional || changing['appointments'].hasChanged || changing['appointments'].additionalActions?.length
                ? <AppointmentEditForm 
                    data={changing['appointments'].value} 
                    openPaymentModal={changing['appointments'].additionalActions?.includes('edit-payment')} 
                    createInvoice={changing['appointments'].additionalActions?.includes('create-invoice')} 
                />
                : <AppointmentProfessionalView data={changing['appointments'].value} />)
        : changing['clientPacks'] ? (changing['clientPacks'].action === 'creating' 
            ? <ClientPackCreateForm packSettings={packSettings}/>
            : <ClientPackEditForm packSettings={packSettings}/>)
        : changing['clients'] ? (changing['clients'].action === 'creating' 
            ? <ClientCreateForm />
            : <ClientEditForm data={changing['clients'].value} />)
        : changing['professionals'] ? (changing['professionals'].action === 'creating' 
            ? <ProfessionalCreateForm />
            : <ProfessionalEditForm data={changing['professionals'].value} />)
        : changing['users'] ? (changing['users'].action === 'creating' 
            ? <UserCreateForm />
            : <UserEditForm data={changing['users'].value} />)
        : changing['expenses'] ? (changing['expenses'].action === 'creating'
            ? <ExpenseCreateForm />
            : <ExpenseEditForm data={changing['expenses'].value} />)
        : changing['stock'] ? (changing['stock'].action === 'creating'
            ? <StockItemCreateForm />
            : <StockItemEditForm data={changing['stock'].value} />)
        : changing['orders'] ? <ClientOrderEditForm />
        : <></>

    const size =
        !changing ? 'xl'
        : changing['reports'] ? 'xl'
        : changing['appointments'] ? 'xl'
        : changing['clientPacks']? 'xl'
        : changing['clients'] ? 'xl'
        : changing['professionals'] ? 'xl'
        : changing['users'] ? 'xl'
        : changing['expenses'] ? 'lg'
        : changing['stock'] ? 'lg'
        : changing['orders'] ? 'lg'
        : 'xl'

    const hideOnAccountTab = onAccountUsersTab && (topEntityType === 'users' || topEntityType === 'professionals');
    const hide = topEntityType && topEntity?.action !== 'creating' && (hideEditionPopupForPages[topEntityType].includes(location.pathname) || hideOnAccountTab)

    const modalMaxWidth = size === 'lg' ? '800px' : '1140px'
    const containerRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)

    const isOpen = !!(topEntityType && topEntity && changing && form && !hide)
    const zIndex = useModalZIndex(isOpen)

    return (
        <>
            { isOpen && (
                <div
                    ref={containerRef}
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        zIndex,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        padding: 'var(--spacer-M)',
                        overflowY: 'auto',
                    }}
                    onClick={topEntity.hasChanged ? undefined : onClose}
                >
                    <div
                        ref={innerRef}
                        style={{
                            backgroundColor: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            maxWidth: modalMaxWidth,
                            borderRadius: '0.3rem',
                            boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.5)',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 'var(--spacer-S) var(--spacer-S)',
                            borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                            gap: 'var(--spacer-XS)',
                            flexShrink: 0,
                        }}>
                            <FullscreenToggle containerRef={containerRef} innerRef={innerRef} modalMaxWidth={modalMaxWidth} />
                            <span className="modal-title h5" style={{flex: 1, marginBottom: 0, display: 'flex', alignItems: 'center'}}>
                                {!!myUser && getModalTitle(topEntityType, topEntity, t, myUser, generalSettings)}
                            </span>
                            {!topEntity.hasChanged && (
                                <IconButton size="small" onClick={onClose}>
                                    <span aria-hidden="true" style={{fontSize: '1.5rem', lineHeight: 1}}>&times;</span>
                                </IconButton>
                            )}
                        </div>
                        <div style={{padding: 'var(--spacer-S)', overflowX: 'auto', minWidth: 0}}>
                            {form}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default EntityModal;
